import { BusyWatcher, Router, ScreenBase } from "@frui.ts/screens";
import { validate, isValid } from "@frui.ts/validation";
import { action, computed, observable, reaction } from "mobx";
import LocalizationService from "services/localizationService";
import AppRouter from "services/appRouter";
import VehicleInfoViewModel from "viewModels/common/vehicleInfoViewModel";
import { interfaces } from "inversify";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import ChargeOrderLicenseItem, {
  CountryIdValidationRules,
  LicensePlateConfirmationValidationRules,
  LicensePlateValidationRules,
  ChargeOrderLicenseItemValidationRules,
} from "models/chargeOrderLicenseItem";
import { scrollToElement, scrollToFirstError } from "utils/scrollTo";
import type { ErrorMessage } from "viewModels/common/types";
import VehicleRepository from "repositories/vehicleRepository";
import ChargeOrderInstructionsResponse from "entities/chargeOrderInstructionsResponse";
import ChargeOrderService from "services/chargeOrderService";
import EnumService from "services/enumService";
import ExemptionReasonResponse from "entities/exemptionReasonResponse";
import ChargeVehicleWarningReason from "entities/chargeVehicleWarningReason";
import ExemptionReason from "entities/exemptionReason";
import ChargeVehicleWarningReasonOrder from "entities/chargeVehicleWarningReasonOrder";
import FuelTypeViewModel from "./fuelTypeViewModel";
import OrderWizardViewModel from "../orderWizardModel";

export const LicenseViewNavigationName = "license";

@Router.registerRoute({
  name: Router.Self,
  route: LicenseViewNavigationName,
})
export default class LicenseViewModel extends ScreenBase {
  public vehicleInfoViewModel: VehicleInfoViewModel;
  private unsubscriptions: Array<() => void> = [];

  @observable errorMessage?: ErrorMessage;
  @observable step?: number;
  @observable loading?: boolean = false;
  @observable formElement?: HTMLFormElement;
  @observable instructions?: ChargeOrderInstructionsResponse;
  busyWatcher = new BusyWatcher();

  constructor(
    public chargeOrderLicenseItem: ChargeOrderLicenseItem,
    public localizationService: LocalizationService,
    private router: AppRouter,
    private vehicleRepository: VehicleRepository,
    private chargeOrderService: ChargeOrderService,
    private enumService: EnumService,
    private vehicleInfoModelFactory: ReturnType<typeof VehicleInfoViewModel.Factory>
  ) {
    super();
    this.navigationName = LicenseViewNavigationName;
    this.nameValue = "eshop.title";
    this.vehicleInfoViewModel = this.vehicleInfoModelFactory(this.chargeOrderLicenseItem, {
      attachValidator: true,
      licensePlateConfirmation: true,
      loadCountriesOnInit: true,
      maxLicensePlateSize: ChargeOrderLicenseItemValidationRules.licensePlate?.hasRequiredLength.max,
    });
    attachAutomaticBlurValidator(this.chargeOrderLicenseItem, {
      ...LicensePlateValidationRules,
      ...LicensePlateConfirmationValidationRules,
      ...CountryIdValidationRules,
    });
  }
  protected onInitialize(): Promise<any> | void {
    this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    this.step = 1;
    this.loading = false;
    this.unsubscriptions.push(
      reaction(() => [this.chargeOrderLicenseItem.countryId, this.chargeOrderLicenseItem.licensePlate], this.onChangeLicenseItem)
    );
    return super.onInitialize();
  }

  protected async onActivate() {
    if (this.stepper) {
      scrollToElement(this.stepper);
    }
    this.errorMessage = undefined;
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get isDisabled() {
    return isValid(this.chargeOrderLicenseItem);
  }

  @computed
  get exemptionReasonWarnings() {
    if (this.instructions instanceof ChargeOrderInstructionsResponse) {
      return this.instructions._possibleExemptionReason;
    }
    return [];
  }

  @computed
  get allowResetOrderByLicensePlate() {
    return (
      this.chargeOrderService.chargeOrderItem.licensePlate &&
      this.chargeOrderService.chargeOrderItem._licensePlateConfirmation &&
      this.chargeOrderLicenseItem.licensePlate !== this.chargeOrderService.chargeOrderItem.licensePlate &&
      this.chargeOrderLicenseItem._licensePlateConfirmation !== this.chargeOrderService.chargeOrderItem._licensePlateConfirmation
    );
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @action.bound
  translateExemptionReasonWarnings(code: string): string {
    if (code === "IDENTIFIED_POSSIBLE_EXEMPTION") {
      if (
        !this.instructions ||
        !this.instructions.possibleExemptionReasonIds ||
        this.instructions.possibleExemptionReasonIds.length < 1
      ) {
        return this.localizationService.translateGeneral("errors.generic");
      }
      const possibleExemptionReasonId = this.instructions.possibleExemptionReasonIds[0];
      const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
      const matchingReason = exemptionReasons.find(x => x.id === possibleExemptionReasonId);
      switch (matchingReason?.exemptionReason) {
        case ExemptionReason.C:
          return this.translateExemptionReasonWarnings("IDENTIFIED_POSSIBLE_EXEMPTION_C");
        case ExemptionReason.P:
          return this.translateExemptionReasonWarnings("IDENTIFIED_POSSIBLE_EXEMPTION_P");
        case ExemptionReason.O:
        case ExemptionReason.O1:
        case ExemptionReason.O2:
          return this.translateExemptionReasonWarnings("IDENTIFIED_POSSIBLE_EXEMPTION_O");
        default:
          return this.localizationService.translateGeneral("errors.generic");
      }
    }
    return this.localizationService.translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`);
  }

  @action.bound
  private resolveRegistrationWarning(response: ChargeOrderInstructionsResponse) {
    const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    const filteredExemptionReason = response.possibleExemptionReasonIds
      ?.map(id => exemptionReasons.find(reason => reason.id === id)?.exemptionReason)
      .map(reason => {
        if (reason?.includes(ExemptionReason.O)) {
          return ExemptionReason.O;
        }
        return reason;
      })
      .filter((value, index, self) => {
        return self.indexOf(value) === index;
      });
    if (response.warningReasons?.includes(ChargeVehicleWarningReason.IdentifiedPossibleExemption)) {
      const warningReasons = response.warningReasons.filter(
        reason => reason !== ChargeVehicleWarningReason.IdentifiedPossibleExemption
      );
      const possibleExemptionReason = filteredExemptionReason?.map(reason => {
        return `${ChargeVehicleWarningReason.IdentifiedPossibleExemption}_${reason}`;
      });
      if (possibleExemptionReason?.length) {
        response._possibleExemptionReason = [...warningReasons, ...possibleExemptionReason];
      }
    } else {
      response._possibleExemptionReason = response.warningReasons;
    }
    response._possibleExemptionReason = response._possibleExemptionReason?.sort((a, b) => {
      return (
        ChargeVehicleWarningReasonOrder[a as keyof ChargeVehicleWarningReasonOrder] -
        ChargeVehicleWarningReasonOrder[b as keyof ChargeVehicleWarningReasonOrder]
      );
    });
    return response;
  }

  @action.bound
  public async onContinue() {
    if (this.step === 1) {
      this.vehicleRegistrationsReaction();
      return;
    }
    if (this.step === 2 && !this.validateAll()) {
      return;
    }
    await this.updateChargeOrderItem();
    this.router.navigate(FuelTypeViewModel);
  }

  @action.bound
  private onChangeLicenseItem() {
    this.step = 1;
    this.instructions = undefined;
  }

  @action.bound
  public async vehicleRegistrationsReaction() {
    const { countryId, licensePlate } = this.chargeOrderLicenseItem;
    const isValid = validate(this.chargeOrderLicenseItem);

    if (countryId && licensePlate && isValid) {
      try {
        this.loading = true;
        const response = await this.vehicleRepository.getChargeOrderInstruction(countryId, licensePlate);
        if (response instanceof ChargeOrderInstructionsResponse) {
          this.instructions = this.resolveRegistrationWarning(response);
        }
        this.step = 2;

        if (!this.instructions?.warningReasons?.length && !this.instructions?.possibleExemptionReasonIds?.length) {
          this.onContinue();
        }
      } catch (e) {
        // Silence catch of background process
      } finally {
        this.loading = false;
      }
    }
  }

  @action.bound
  public async updateChargeOrderItem() {
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;

    chargeOrderItem.licensePlate = this.chargeOrderLicenseItem.licensePlate;
    chargeOrderItem._licensePlateConfirmation = this.chargeOrderLicenseItem._licensePlateConfirmation;
    chargeOrderItem.countryId = this.chargeOrderLicenseItem.countryId;

    if (this.instructions instanceof ChargeOrderInstructionsResponse && this.instructions.fuelTypeLimitation) {
      chargeOrderItem._fuelTypeLimitation = this.instructions?.fuelTypeLimitation;
    } else {
      chargeOrderItem._fuelTypeLimitation = undefined;
    }

    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  public validateAll() {
    this.errorMessage = undefined;

    const isValid = validate(this.chargeOrderLicenseItem);

    if (!isValid) {
      this.errorMessage = "invalidData";
      scrollToFirstError(this.formElement);
    }

    return isValid;
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrderLicenseItem: ChargeOrderLicenseItem) =>
      new LicenseViewModel(
        chargeOrderLicenseItem,
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(VehicleRepository),
        container.get(ChargeOrderService),
        container.get(EnumService),
        container.get(VehicleInfoViewModel.Factory)
      );
  }
}
